/* eslint-disable factorial/no-raw-html */
import * as React from 'react'

import { Box, Button, IllustrationType, Text } from '@factorialco/deprecated-design-system'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import useCreateTrialModuleSubscription from 'modules/marketplace_modules/hooks/useCreateTrialModuleSubscription'
import { useMarketplaceModulesPath } from 'modules/marketplace_modules/hooks/useMarketplaceModulesPath'
import { useGetProductUpdateModules } from 'modules/product_updates/hooks/useGetProductUpdateModules'
import defaultIcon from 'modules/upsell/components/RequestUpgradeModal/_ModalDescription/icons/default.svg'
import useCreateUpsellRequest from 'modules/upsell/hooks/useCreateUpsellRequest'
import { RequestUpgradeProps } from 'modules/upsell/lib/requestUpgradeTypes'

import { locale } from '../utils/locales'

interface Props {
  onClose: () => void
  moduleData: {
    icon?: IllustrationType
    chargebeeModuleId: string
  }
  requestDetail: RequestUpgradeProps
  description?: string
}

interface DescriptionProps {
  moduleRequestedName: string
  icon: string
}

const ModalDescription = ({ moduleRequestedName, icon = '' }: DescriptionProps) => {
  return (
    <Box alignItems="center" gap="s12">
      <img width="120px" src={icon ? icon : defaultIcon} />
      <Box alignItems="center">
        <Text.Header2>
          {i18n.t('marketplace.modules.trial_upsell.title', { module_name: moduleRequestedName })}
        </Text.Header2>
      </Box>
      <Box alignItems="center" textAlign="center">
        <Text.Normal size="300" overflowWrap>
          {i18n.t('marketplace.modules.trial_upsell.description')}
        </Text.Normal>
      </Box>
    </Box>
  )
}

const TrialModal = ({ onClose, moduleData, requestDetail }: Props) => {
  const { company, access } = useCurrentSession()
  const { getListPath } = useMarketplaceModulesPath()
  const createSubscriptionOverload = useCreateTrialModuleSubscription([company.id])
  const { create } = useCreateUpsellRequest()
  const datoFormatLocale = locale[access.locale]
  const { modules, loading: loadingModule } = useGetProductUpdateModules({
    moduleSlug: requestDetail.moduleRequested,
    locale: datoFormatLocale,
  })
  const [{ name: moduleRequestedName = '', icon: moduleIcon = '' } = {}] = modules

  const getErrorMessage = (error: Record<string, string>) => {
    if (error.expirationDate) {
      return i18n.t('marketplace.modules.trial_upsell.expiration_date_error')
    }
    if (error.errors === 'already_in_trial') {
      return i18n.t('marketplace.modules.trial_upsell.already_in_trial_error')
    }
    return i18n.t('errors.generic_error')
  }

  const handleSubscriptionOverload = async () => {
    const subscriptionOverload = await createSubscriptionOverload({
      chargebeeId: moduleData.chargebeeModuleId,
    })

    const handled = handleResult(subscriptionOverload, (error) => {
      if (!error) return undefined
      app.setFlash({
        type: 'wrong',
        message: getErrorMessage(error),
        time: 5000,
      })
    })

    if (handled.success) {
      await create(requestDetail)
      window.location.href = getListPath()
    }
  }

  useEffect(() => {
    track(trackingEvents.marketplaceModules.openUpsellTrialModal)
  }, [])

  if (loadingModule) return null

  return (
    <Modal
      illustration="crown"
      title={''}
      description={
        <ModalDescription moduleRequestedName={moduleRequestedName} icon={moduleIcon || ''} />
      }
      onClose={onClose}
      footer={
        <Button onClick={handleSubscriptionOverload} type="upgrade">
          {i18n.t('marketplace.modules.trial_upsell.button')}
        </Button>
      }
      background="radial-gradient(50% 85.16% at 50% 0%, #FBE3BC 0%, #FFFFFF 100%)"
      isUpsellModal
    />
  )
}

export default TrialModal
